/**
 * PDF Preview Modal Component
 *
 * Modal for previewing PDF exports before downloading.
 * Uses browser's native PDF viewer via iframe for full toolbar support.
 * On mobile devices, shows a download prompt since iframe PDF viewing is not well supported.
 */

import React, { useMemo, useState, useEffect } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";

export interface PDFPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfData: ArrayBuffer | null;
  title?: string;
  onDownload: () => void;
  isDownloading?: boolean;
}

export const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  isOpen,
  onClose,
  pdfData,
  title: _title,
  onDownload,
  isDownloading = false,
}) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Create blob URL for the PDF to display in iframe
  const pdfUrl = useMemo(() => {
    if (!pdfData) return null;
    const blob = new Blob([pdfData], { type: "application/pdf" });

    return URL.createObjectURL(blob);
  }, [pdfData]);

  // Clean up blob URL when modal closes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  return (
    <Modal
      classNames={{
        base: "max-h-[95vh] w-[95vw] sm:w-[90vw] max-w-5xl",
        body: "p-0",
      }}
      isOpen={isOpen}
      size="5xl"
      onClose={onClose}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Icon icon="vscode-icons:file-type-pdf2" width={24} />
                <span>{t("PDF Preview")}</span>
              </div>
            </ModalHeader>
            <ModalBody>
              {!pdfUrl && (
                <div className="flex items-center justify-center h-[60vh]">
                  <p className="text-default-500">{t("No PDF data available")}</p>
                </div>
              )}

              {/* Mobile: Show download prompt instead of iframe */}
              {pdfUrl && isMobile && (
                <div className="flex flex-col items-center justify-center h-[50vh] gap-6 p-4 text-center">
                  <Icon className="text-default-300" icon="solar:document-bold" width={64} />
                  <div className="space-y-2">
                    <p className="text-default-700 font-medium">
                      {t("PDF preview is not available on mobile")}
                    </p>
                    <p className="text-default-500 text-sm">
                      {t("Download the PDF to view it in your device's PDF reader")}
                    </p>
                  </div>
                  <Button
                    className="btn-brand-primary"
                    isDisabled={isDownloading}
                    isLoading={isDownloading}
                    startContent={!isDownloading && <Icon icon="solar:download-bold" width={20} />}
                    onPress={onDownload}
                  >
                    {isDownloading ? t("Downloading...") : t("Download PDF")}
                  </Button>
                </div>
              )}

              {/* Desktop: Show PDF in iframe */}
              {pdfUrl && !isMobile && (
                <iframe
                  className="w-full h-[75vh] border-0 rounded-lg bg-white"
                  src={pdfUrl}
                  title={t("PDF Preview")}
                />
              )}
            </ModalBody>
            {/* Footer with download button on desktop */}
            {pdfUrl && !isMobile && (
              <ModalFooter className="justify-end border-t border-divider">
                <Button
                  className="btn-brand-primary"
                  isDisabled={isDownloading}
                  isLoading={isDownloading}
                  startContent={!isDownloading && <Icon icon="solar:download-bold" width={20} />}
                  onPress={onDownload}
                >
                  {isDownloading ? t("Downloading...") : t("Download")}
                </Button>
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default PDFPreviewModal;
